import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import './ProjectDeliverableRevenue.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function ProjectDeliverableRevenue() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  useEffect(() => {
    const requiredPath = "/project-budget";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });
    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });
    setCanEdit(hasEditPermission);

    setCanDelete(
      permissions.some((page) =>
        new URL(page.url).pathname === requiredPath && page.can_delete
      )
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          deliverableRevenue,
          offerCodes,
          projectCodes,
          currenciesResponse,
          systemCurrencyResponse
        ] = await Promise.all([
          axiosInstance.get('/project-deliverable-revenue/'),
          axiosInstance.get('/offer-codes/'),
          axiosInstance.get('/project-codes/'),
          axiosInstance.get('/currencies/'),
          axiosInstance.get('/system-currency/'),
        ]);

        const BSOfferCodeMap = offerCodes.data.reduce((acc, item) => {
          acc[item.id] = item.offer_code || '';
          return acc;
        }, {});

        const ProjectCodeMap = projectCodes.data.reduce((acc, item) => {
          acc[item.id] = item.code || '';
          return acc;
        }, {});

        const currenciesMap = currenciesResponse.data.reduce((acc, item) => {
          acc[item.id] = item.abbreviation || '';
          return acc;
        }, {});

        const systemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) && systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency
            : null;

        const systemCurrencyAbbreviation = systemCurrencyId
          ? currenciesMap[systemCurrencyId] || ''
          : '';

        const sortedData = deliverableRevenue.data.map((item) => {
          const currencyAbbreviation = currenciesMap[item.currency] || '';

          const systemCurrency =
            item.revenue_offer_without_vat_system_currency ||
            item.revenue_po_without_vat_system_currency ||
            systemCurrencyAbbreviation;

          return {
            id: item.id,
            codOferta: BSOfferCodeMap[item.offer_code] || '-',
            projectCodes: ProjectCodeMap[item.project_code] || '-',
            deliverableId: item.deliverable_id || '-',
            deliverableDescription: item.deliverable_description || '-',

            revenue_offer_without_vat: item.revenue_offer_without_vat
              ? `${formatNumber(item.revenue_offer_without_vat)} ${currencyAbbreviation}`
              : `0,00 ${currencyAbbreviation}`,

            revenue_offer_without_vat_system: item.revenue_offer_without_vat_system
              ? `${formatNumber(item.revenue_offer_without_vat_system)} ${systemCurrency}`
              : `0,00 ${systemCurrency}`,

            revenue_po_without_vat: item.revenue_po_without_vat
              ? `${formatNumber(item.revenue_po_without_vat)} ${currencyAbbreviation}`
              : `0,00 ${currencyAbbreviation}`,

            revenue_po_without_vat_system: item.revenue_po_without_vat_system
              ? `${formatNumber(item.revenue_po_without_vat_system)} ${systemCurrency}`
              : `0,00 ${systemCurrency}`,
          };
        }).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, []);

  const handleAddClick = () => {
    navigate('/project-budget/project-deliverable-revenue/add-project-deliverable-revenue');
  };

  const handleEditClick = (row) => {
    navigate(`/project-budget/project-deliverable-revenue/edit-project-deliverable-revenue`, {
      state: { id: row.id }
    });
  };

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const columns = [
    { field: 'codOferta', headerName: 'Offer Code', flex: 1, minWidth: 120 },
    { field: 'projectCodes', headerName: 'Project Code', flex: 1, minWidth: 200 },
    { field: 'deliverableId', headerName: 'Deliverable Id', flex: 1, minWidth: 105 },
    { field: 'deliverableDescription', headerName: 'Deliverable Description', flex: 2, minWidth: 250 },

    {
      field: 'revenue_offer_without_vat',
      headerName: 'Revenue offer without VAT',
      flex: 1,
      minWidth: 170,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Revenue offer without VAT
        </div>
      ),
    },

    {
      field: 'revenue_offer_without_vat_system',
      headerName: 'Revenue offer without VAT System',
      flex: 1,
      minWidth: 220,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Revenue offer without VAT System
        </div>
      ),
    },

    {
      field: 'revenue_po_without_vat',
      headerName: 'Revenue PO without VAT',
      flex: 1,
      minWidth: 170,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Revenue PO without VAT
        </div>
      ),
    },

    {
      field: 'revenue_po_without_vat_system',
      headerName: 'Revenue PO without VAT System',
      flex: 1,
      minWidth: 220,
      renderHeader: () => (
        <div style={{
          whiteSpace: 'normal',
          lineHeight: '16px',
          textAlign: 'center'
        }}>
          Revenue PO without VAT System
        </div>
      ),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <div className="project-budget">
      {canCreate && (
        <div className="add-button-container">
          <Button
            id="add-project-deliverable-revenue"
            variant="contained"
            onClick={handleAddClick}
            disabled={!canCreate}
            sx={{ textTransform: "none" }}
            style={{
              float: 'right',
              marginBottom: '15px',
            }}
          >
            <AddIcon />
            Add Project Deliverable Revenue
          </Button>
        </div>
      )}

      <div className="table-container">
        <DataGrid
          id="project-deliverable-revenue-table"
          rows={rows}
          columns={columns}
          columnHeaderHeight={70}
          pageSize={5}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }]
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          disableRowSelectionOnClick={true}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important"
            },
          }}
          pageSizeOptions={[10, 20, 25]}
          rowHeight={40}
        />
      </div>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axiosInstance.delete(`/project-deliverable-revenue/${itemToDelete.id}/`);
                setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                setOpenDeleteDialog(false);
              } catch (error) {
                const message =
                  error.response?.data?.error ||
                  error.response?.data?.detail ||
                  "An unexpected error occurred while deleting.";
                setDeleteErrorMessage(message);
                setOpenDeleteDialog(false);
                setErrorDialogOpen(true);
              }
            }}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b71c1c',
              }
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" style={{ color: 'red' }}>
          Delete Failed
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {deleteErrorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ProjectDeliverableRevenue;